/**
 * R1 — Media coverage section. Slots into any outbreak detail panel.
 * Shows:
 *   - per-window coverage rating (silent / low / medium / high) via CovBadge
 *   - time-window toggle (7d / 30d / since onset; default-by-age)
 *   - asymmetry flag (UNDER-REPORTED / HIGH-PROFILE) when present
 *   - inline source-type breakdown (agency / news / social, 3 mini-bars)
 *   - foot: confidence split (N high · M ambiguous mapping)
 *
 * R4 (empty state) is in the same file — CoverageEmpty — because the two
 * always ship together (per the CD recommendation: "ship R4 with R1").
 */

import { useState } from 'react';
import { CovBars, CovBadge, AsymFlag } from './primitives';
import { computeAsymmetry, type OutbreakCoverage, type CoverageLevel, type CoverageWindow } from './types';

const THREAT_TIER_WORDS = ['ROUTINE', 'CRITICAL', 'HIGH', 'ELEVATED', 'WATCH'] as const;

/** Threat-tier → expected-coverage. Currently 1:1 ordinal; lift into the
 *  threat-tier engine when it becomes a calibrated mapping. */
const EXPECTED_COVERAGE_BY_TIER: Record<number, CoverageLevel> = {
  1: 3, 2: 2, 3: 2, 4: 1, 0: 0,
};

/** Default window picker — per CD spec: outbreak <14d old → 7d; else 30d.
 *  Caller passes outbreak-age in days (if known). */
export function defaultWindow(outbreakAgeDays?: number): CoverageWindow {
  if (outbreakAgeDays != null && outbreakAgeDays < 14) return 'd7';
  return 'd30';
}

export interface MediaCoverageSectionProps {
  coverage: OutbreakCoverage;
  /** Threat tier (1-4 + 0). Drives expected-coverage for the asymmetry flag. */
  threatTier: 0 | 1 | 2 | 3 | 4;
  /** Open the source drill-down side-pane (R2). Caller wires up. */
  onShowSources?: () => void;
  /** Optional default-window override; otherwise computed from coverage state. */
  initialWindow?: CoverageWindow;
}

export function MediaCoverageSection({
  coverage, threatTier, onShowSources, initialWindow,
}: MediaCoverageSectionProps) {
  if (coverage.noData) return <CoverageEmpty />;

  const [win, setWin] = useState<CoverageWindow>(initialWindow ?? 'd7');
  const snap = coverage.windows[win];
  const expected = EXPECTED_COVERAGE_BY_TIER[threatTier] ?? 0;
  const asym = computeAsymmetry(snap.level, expected);
  const tierWord = THREAT_TIER_WORDS[threatTier] ?? 'ROUTINE';
  const totalByType = coverage.byType.agency + coverage.byType.news + coverage.byType.social;

  return (
    <section
      style={{
        marginTop: 16,
        padding: 14,
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-md)',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 600,
          letterSpacing: '.5px', textTransform: 'uppercase', color: 'var(--bm-text-muted)',
        }}>Media coverage</span>
        <div style={{ display: 'inline-flex', gap: 0, border: '1px solid var(--bm-border)', borderRadius: 6 }}>
          {([['d7', '7d'], ['d30', '30d'], ['onset', 'since onset']] as const).map(([k, l]) => (
            <button
              key={k}
              type="button"
              onClick={() => setWin(k)}
              style={{
                padding: '4px 10px',
                fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 600,
                border: 'none', cursor: 'pointer',
                background: win === k ? 'var(--bm-bg-strong)' : 'transparent',
                color: win === k ? 'var(--bm-text)' : 'var(--bm-text-subtle)',
              }}
            >{l}</button>
          ))}
        </div>
      </header>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <CovBadge level={snap.level} count={snap.count} onClick={onShowSources} />
        {asym && (
          <AsymFlag
            asymmetry={asym}
            observedLevel={snap.level}
            expectedLevel={expected}
            threatTierWord={tierWord}
          />
        )}
      </div>

      {/* Source-type breakdown — inline 3-bar mini-chart, always visible */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 14 }}>
        {([
          ['agency', 'Agency', coverage.byType.agency],
          ['news', 'News', coverage.byType.news],
          ['social', 'Social', coverage.byType.social],
        ] as const).map(([k, label, n]) => (
          <div key={k}>
            <div style={{
              height: 4, borderRadius: 2,
              background: 'var(--bm-bg-strong)',
              position: 'relative', overflow: 'hidden',
            }}>
              <span style={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: totalByType ? `${(n / totalByType) * 100}%` : '0%',
                background: 'var(--bm-cov)',
              }} />
            </div>
            <div style={{
              marginTop: 4,
              fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-subtle)',
            }}>{label} <b style={{ color: 'var(--bm-text-muted)' }}>{n}</b></div>
          </div>
        ))}
      </div>

      <footer style={{
        marginTop: 12, paddingTop: 10,
        borderTop: '1px solid var(--bm-border)',
        display: 'flex', justifyContent: 'space-between',
        fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-subtle)',
      }}>
        <span>coverage · illustrative · synthetic sample data</span>
        <span>confidence: {coverage.highConfidenceCount} high · {coverage.ambiguousCount} ambiguous mapping</span>
      </footer>
    </section>
  );
}

/** R4 — honest empty state. "No coverage data ingested" ≠ "silent". Those are
 *  opposite findings; conflating them would mislead operators into treating
 *  a pipeline gap as an absence-of-outbreak signal. Non-negotiable per CD. */
export function CoverageEmpty() {
  return (
    <section
      style={{
        marginTop: 16,
        padding: 14,
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-md)',
      }}
    >
      <header style={{ marginBottom: 12 }}>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 600,
          letterSpacing: '.5px', textTransform: 'uppercase', color: 'var(--bm-text-muted)',
        }}>Media coverage</span>
      </header>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <CovBars level={0} />
        <div>
          <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--bm-text-muted)' }}>No coverage data ingested</div>
          <div style={{ fontSize: 12, color: 'var(--bm-text-subtle)', marginTop: 4, lineHeight: 1.5 }}>
            Media feeds (ProMED · WHO situation reports · GDELT) not yet linked for this outbreak.
            Absence of data is not absence of coverage — this is a pipeline gap, not a "silent" finding.
          </div>
        </div>
      </div>
    </section>
  );
}
